/**
 * UsersListPage — route `/users`.
 *
 * SCRUM-108 Phase 1: the real org-roster page. Replaces the PR #134
 * "self-only" placeholder. Pulls from `GET /organizations/:slug/users`
 * via `useUsersList`; offers q / role / status filters, sort, and
 * pagination. Forbidden / error branches render inline alerts rather
 * than the legacy Retry-only flow.
 *
 * Notes:
 *  - Route-level permission is declared in `apps/admin/src/app/router.tsx`
 *    as `organization-permission ['identity.users.read']`. The page also
 *    self-handles 403 (the `forbidden` branch) because route declarations
 *    are advisory until the runtime guard ships; a permission revocation
 *    between page mount and BE call will surface here.
 *  - `<Avatar />` stays Tier 3 (kit) — the earlier promotion (ADR
 *    `frontend-0004`) is preserved.
 *  - Status label copy is local English for Phase 1; i18n is a doc-known
 *    follow-up.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, TextField, type BadgeVariant } from '@sparta/ui-kit';
import { useUsersList, type UsersListSort } from '../api/use-users-list.js';
import { UsersHeader } from '../_components/UsersHeader.js';
import type { OrgMemberRow } from '../api/types.js';

const PER_PAGE = 50;

type StatusFilter = 'any' | 'active' | 'pending_verification' | 'suspended';

// 'role' is intentionally omitted from sortable columns. The BE repo's
// `case 'role'` (apps/api/src/modules/identity/infrastructure/user/
// prisma-org-member-query.repository.ts) sorts by *count* of attached
// roles, not alphabetic slug — Prisma 5's `orderBy` on a to-many relation
// only supports `_count`. Every member currently has exactly one role,
// so the sort is a no-op end-to-end. Re-enable once the BE switches to
// `ORDER BY MIN(role.slug)` (tracked as a phase-E follow-up in the repo
// file). The Role column is still rendered — just not made interactive.
const SORTABLE_COLUMNS = ['email', 'status', 'last_seen'] as const;

// Role + name are *display* columns without a sort affordance — see the
// SORTABLE_COLUMNS comment above. ColumnId widens the union so the
// shared label / aria-sort / render helpers stay typed.
type ColumnId = (typeof SORTABLE_COLUMNS)[number] | 'role' | 'name';

// Column order mirrors the table header layout.
const COLUMN_ORDER: readonly ColumnId[] = [
  'name',
  'email',
  'role',
  'status',
  'last_seen',
];

const COLUMN_LABELS: Record<ColumnId, string> = {
  name: 'Name',
  email: 'Email',
  role: 'Role',
  status: 'Status',
  last_seen: 'Last seen',
};

function formatName(firstName: string | null, lastName: string | null): string {
  const parts = [firstName, lastName].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : '—';
}

function isSortableColumn(
  column: ColumnId,
): column is (typeof SORTABLE_COLUMNS)[number] {
  return (SORTABLE_COLUMNS as readonly string[]).includes(column);
}

function statusLabel(status: OrgMemberRow['status']): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'pending_verification':
      return 'Pending verification';
    case 'suspended':
      return 'Suspended';
  }
}

// SCRUM-115: maps the row's status to the kit `Badge` variant. This
// usage is the cross-slice consumer that fires Badge's Tier 0 → Tier 3
// promotion trigger (identity module → @sparta/ui-kit).
function statusBadgeVariant(status: OrgMemberRow['status']): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending_verification':
      return 'warning';
    case 'suspended':
      return 'danger';
  }
}

function formatLastSeen(lastSeenAt: string | null): string {
  if (lastSeenAt === null) return 'Never';
  const parsed = new Date(lastSeenAt);
  if (Number.isNaN(parsed.getTime())) return lastSeenAt;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function ariaSortFor(
  column: ColumnId,
  sort: UsersListSort,
): 'ascending' | 'descending' | 'none' {
  if (!isSortableColumn(column)) return 'none';
  if (sort.field !== column) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

export function UsersListPage(): React.ReactElement {
  const navigate = useNavigate();

  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState<StatusFilter>('any');
  const [sort, setSort] = React.useState<UsersListSort>({
    field: 'last_seen',
    dir: 'desc',
  });
  const [page, setPage] = React.useState(1);

  const filters = React.useMemo(
    () => ({
      q: q.length > 0 ? q : undefined,
      status: status === 'any' ? undefined : status,
    }),
    [q, status],
  );

  const result = useUsersList({
    filters,
    sort,
    page,
    perPage: PER_PAGE,
  });

  if (result.status === 'loading') {
    return (
      <section
        aria-busy="true"
        aria-label="Loading users"
        className="pt-2 text-sm text-[var(--fg-tertiary)]"
      >
        Loading users…
      </section>
    );
  }

  const cycleSort = (column: (typeof SORTABLE_COLUMNS)[number]): void => {
    setSort((current) => {
      if (current.field !== column) {
        return { field: column, dir: 'asc' };
      }
      return { field: column, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    });
    setPage(1);
  };

  const onSearchChange = (value: string): void => {
    setQ(value);
    setPage(1);
  };

  const onStatusChange = (value: StatusFilter): void => {
    setStatus(value);
    setPage(1);
  };

  const showCount =
    result.status === 'ready'
      ? `${result.data.length} of ${result.total}`
      : '—';
  const totalPages =
    result.status === 'ready' ? Math.max(1, Math.ceil(result.total / PER_PAGE)) : 1;
  const canGoPrev = page > 1;
  const canGoNext = result.status === 'ready' && page < totalPages;

  return (
    <section aria-labelledby="users-page-title" className="flex flex-col gap-4">
      <UsersHeader activeTab="members" count={showCount} />

      <div
        role="search"
        aria-label="Filter users"
        className="flex flex-wrap items-end gap-3"
      >
        <TextField
          label="Search"
          type="search"
          value={q}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Email, name, role…"
          className="min-w-64"
        />
        {/*
         * Status filter mirrors the bounding-box shape of TextField
         * (kit primitive — no Select equivalent yet): outer wrapper
         * carries `mb-4` so the parent `items-end` aligns by the same
         * baseline as the search input, and the label uses the same
         * `mb-2 text-[13px] font-semibold` treatment so the two
         * controls read as a matched pair in the filter row.
         */}
        <div className="mb-4 block">
          <label
            htmlFor="users-filter-status"
            className="mb-2 block text-[13px] font-semibold text-[var(--fg-secondary)]"
          >
            Status
          </label>
          <select
            id="users-filter-status"
            aria-label="Filter by status"
            value={status}
            onChange={(e) => onStatusChange(e.target.value as StatusFilter)}
            // min-h-touch mirrors TextField's md-size class so the
            // select snaps to the 44px touch floor (CLAUDE.md §7) and
            // matches the input row height. rounded-xl matches
            // TextField's INPUT_BASE_CLASSES (was rounded-md before;
            // the smaller radius looked off next to the input).
            className="block min-h-touch rounded-xl border border-solid border-[var(--border-default)] bg-[var(--bg-surface)] px-3 text-[15px] text-[var(--fg-primary)]"
          >
            <option value="any">Any</option>
            <option value="active">Active</option>
            <option value="pending_verification">Pending verification</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {result.status === 'forbidden' ? (
        <div
          role="alert"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          You don’t have permission to view the members of this organization.
          Ask an organization owner or admin if you need access.
        </div>
      ) : null}

      {result.status === 'error' ? (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          <p>We couldn’t load the member list. The backend may be unreachable.</p>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => result.refetch()}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {result.status === 'ready' ? (
        <>
          <table
            aria-label="Members of this organization"
            className="w-full text-left text-sm"
          >
            <thead>
              <tr className="border-b border-[var(--border-subtle)] text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
                {COLUMN_ORDER.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    aria-sort={ariaSortFor(column, sort)}
                    className="py-3 pr-4"
                  >
                    {isSortableColumn(column) ? (
                      <>
                        {/*
                         * ADR frontend-0004 exemption: column-sort trigger
                         * lives inside a <th> with the load-bearing
                         * `aria-sort` attribute. Composing kit <Button>
                         * would (a) add background chrome that doesn't
                         * belong in a table header, (b) lose the bare
                         * uppercase typographic treatment that signals
                         * "header" to readers, and (c) collide with the
                         * 44 px touch floor in a 28 px row. Mirrors the
                         * AnchorPills exemption rationale.
                         */}
                        {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
                        <button
                          type="button"
                          onClick={() => cycleSort(column)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)] hover:text-[var(--fg-primary)] focus-visible:outline-2 focus-visible:outline-[var(--brand-500)] focus-visible:outline-offset-2"
                        >
                          {COLUMN_LABELS[column]}
                          {sort.field === column ? (
                            <span aria-hidden="true">
                              {sort.dir === 'asc' ? '↑' : '↓'}
                            </span>
                          ) : null}
                        </button>
                      </>
                    ) : (
                      // Non-sortable column: bare label, no button. Matches
                      // the typography of the sortable headers without
                      // claiming an affordance the BE cannot honour.
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
                        {COLUMN_LABELS[column]}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.data.map((row) => {
                const openDetail = (): void => {
                  navigate(`/users/${row.userId}`);
                };
                return (
                  <tr
                    key={row.userId}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open ${row.email}`}
                    onClick={openDetail}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDetail();
                      }
                    }}
                    className="cursor-pointer border-b border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--brand-500)] focus-visible:outline-offset-2"
                  >
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3 text-[var(--fg-primary)]">
                        <Avatar name={row.firstName !== null ? `${row.firstName} ${row.lastName ?? ''}`.trim() : row.email} size={32} />
                        <span className="truncate">
                          {formatName(row.firstName, row.lastName)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      <span className="truncate">{row.email}</span>
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      {row.primaryRoleDisplayName ?? '(no role)'}
                    </td>
                    <td className="py-3 pr-4">
                      <Badge variant={statusBadgeVariant(row.status)} size="sm">
                        {statusLabel(row.status)}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      {formatLastSeen(row.lastSeenAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <nav
            aria-label="Pagination"
            className="flex items-center justify-between gap-3 pt-2 text-sm text-[var(--fg-secondary)]"
          >
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                aria-label="Previous page"
                disabled={!canGoPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label="Next page"
                disabled={!canGoNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
